import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LEGAL_FORMS } from "@/lib/format";

export const ConsentNotice = ({ orgId, onDone, current }) => {
  const { loadOrgs } = useAuth();
  const [ai, setAi] = useState(current?.ai_processing || false);
  const [ok, setOk] = useState(current?.accepted || false);
  const save = async () => {
    try {
      await api.post(`/orgs/${orgId}/consent`, { accepted: ok, ai_processing: ai });
      toast.success("Document handling preferences saved");
      await loadOrgs();
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <section className="card space-y-4 p-6" data-testid="consent-notice">
      <h2 className="font-semibold text-[#0B192C]">How we handle your documents</h2>
      <ul className="list-disc space-y-1.5 pl-5 text-sm text-slate-700">
        <li>Files are encrypted before they are stored, and are only accessible to members of this workspace.</li>
        <li>Text is extracted on our servers to find facts (such as company number or turnover). Extracted facts are labelled and only used when you confirm them.</li>
        <li>Documents are kept until you delete them or close the workspace. Deleted documents are removed from the workspace immediately and purged from backups within 30 days.</li>
        <li>External AI processing is <b>off by default</b>. If enabled, only the facts needed for a requested suggestion are sent to OpenAI via our AI provider. Customer inventions are not used to train models.</li>
        <li>Every upload, download and share is recorded in the activity log.</li>
      </ul>
      <label className="flex items-start gap-3 text-sm"><input type="checkbox" data-testid="consent-accept-checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} className="mt-1 h-4 w-4 accent-[#0D9488]" />I have read this notice and want to upload documents to this workspace.</label>
      <label className="flex items-start gap-3 text-sm"><input type="checkbox" data-testid="consent-ai-checkbox" checked={ai} onChange={(e) => setAi(e.target.checked)} className="mt-1 h-4 w-4 accent-[#0D9488]" />Allow optional AI drafting suggestions (external processing, on request only).</label>
      <button onClick={save} data-testid="consent-save-button" className="btn-teal btn-sm">Save preferences</button>
    </section>
  );
};

export default function Onboarding() {
  const { loadOrgs, setOrgId } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [legal, setLegal] = useState("");
  const [org, setOrg] = useState(null);
  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/orgs", { name, legal_form: legal || null });
      await loadOrgs();
      setOrgId(data.id);
      setOrg(data);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div className="mx-auto max-w-2xl fade-up">
      <p className="caption text-[#0D9488]">Step 1 of 7 · Tell us about your business</p>
      <h1 className="mt-2 text-3xl font-bold text-[#0B192C]">Create your workspace</h1>
      <p className="mt-2 text-sm text-slate-600">A workspace holds one business profile, reused across every project and application. Not incorporated yet? Use your own or a working name.</p>
      {!org ? (
        <form onSubmit={create} className="card mt-8 space-y-5 p-6" data-testid="onboarding-form">
          <div><label className="label" htmlFor="o-name">Business or working name</label><input id="o-name" required data-testid="onboarding-name-input" className="input" value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div><label className="label" htmlFor="o-legal">Legal form</label>
            <select id="o-legal" data-testid="onboarding-legal-select" className="input" value={legal} onChange={(e) => setLegal(e.target.value)}>
              <option value="">Select…</option>{Object.entries(LEGAL_FORMS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <button type="submit" data-testid="onboarding-create-button" className="btn-teal">Create workspace</button>
        </form>
      ) : (
        <div className="mt-8 space-y-4">
          <ConsentNotice orgId={org.id} onDone={() => navigate("/app/business")} />
          <button onClick={() => navigate("/app/business")} data-testid="onboarding-skip-consent" className="text-sm text-slate-500 hover:underline">Decide later (uploads stay disabled)</button>
        </div>
      )}
    </div>
  );
}
